/**
 * API client. Unwraps the `{ data }` envelope, throws a typed
 * `ApiClientError` for `{ error }`, and transparently refreshes an expired
 * access token exactly once per request.
 *
 * The active shop rides on every request as `X-Pospak-Shop` so the server
 * always knows which outlet the user is acting on (docs/CONVENTIONS.md).
 */
import { SHOP_HEADER } from '@pospak/shared';

const BASE = '/api';

export class ApiClientError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** Field-level messages from a VALIDATION_ERROR, for react-hook-form. */
  get fieldErrors(): Record<string, string> {
    if (this.code !== 'VALIDATION_ERROR' || !Array.isArray(this.details)) return {};
    const out: Record<string, string> = {};
    for (const d of this.details as { path: string; message: string }[]) {
      const field = d.path.replace(/^body\./, '');
      out[field] ??= d.message;
    }
    return out;
  }
}

type TokenGetter = () => { accessToken: string | null; shopId: string | null };

/**
 * Outcome of a token refresh.
 *
 * `fatal` separates "this session is genuinely over, sign them out" from "the
 * server could not answer right now, keep the session". The API's auth rate
 * limiter answers 429, and treating that like a rejected token is what used to
 * dump a cashier back to the login screen mid-shift.
 */
export type RefreshOutcome =
  | { ok: true; accessToken: string }
  | { ok: false; fatal: boolean };

type RefreshFn = () => Promise<RefreshOutcome>;
type UnauthorizedFn = () => void;

let getTokens: TokenGetter = () => ({ accessToken: null, shopId: null });
let refreshTokens: RefreshFn = async () => ({ ok: false, fatal: true });
let onUnauthorized: UnauthorizedFn = () => {};

/** Wired once by the auth store so this module has no import cycle. */
export function configureApi(options: {
  getTokens: TokenGetter;
  refresh: RefreshFn;
  onUnauthorized: UnauthorizedFn;
}): void {
  getTokens = options.getTokens;
  refreshTokens = options.refresh;
  onUnauthorized = options.onUnauthorized;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
  /** Skip the shop header for business-wide endpoints. */
  noShop?: boolean;
}

async function raw(path: string, options: RequestOptions, token: string | null): Promise<Response> {
  const { shopId } = getTokens();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (shopId && !options.noShop) headers[SHOP_HEADER] = shopId;

  return fetch(`${BASE}${path}`, {
    method: options.method ?? 'GET',
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal,
  });
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { accessToken } = getTokens();
  let response = await raw(path, options, accessToken);

  // One transparent refresh. Only a GENUINE auth failure bounces to login: a
  // 429 from the rate limiter, a 5xx, or an offline terminal keeps the session
  // and lets the original 401 surface as an ordinary request error, so the
  // next attempt recovers instead of forcing a re-login.
  if (response.status === 401 && accessToken) {
    const outcome = await refreshTokens();
    if (outcome.ok) {
      response = await raw(path, options, outcome.accessToken);
    } else if (outcome.fatal) {
      onUnauthorized();
    }
  }

  if (response.status === 204) return undefined as T;

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (!response.ok) {
    const body = payload as { error?: { code: string; message: string; details?: unknown } } | null;
    // 423 ACCESS_BLOCKED (an owner suspended this user) is deliberately NOT a
    // sign-out: the message on screen is the point, and clearing the session
    // would replace it with a bare login form.
    if (response.status === 401) onUnauthorized();
    throw new ApiClientError(
      response.status,
      body?.error?.code ?? 'UNKNOWN',
      body?.error?.message ?? 'Something went wrong',
      body?.error?.details,
    );
  }

  return (payload as { data: T }).data;
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'DELETE', body }),
};

export interface UploadedFile {
  id: string;
  originalName: string;
  mimeType: string;
  sizeBytes: number;
}

/**
 * `POST /files` — the one multipart request in the app (README.md Phase 18).
 * Separate from `api.post` because that always sends JSON; a browser sets
 * its own `multipart/form-data` boundary header, which `raw()`'s hardcoded
 * `Content-Type: application/json` would otherwise stomp on.
 */
export async function uploadFile(path: string, file: File): Promise<UploadedFile> {
  const { accessToken, shopId } = getTokens();
  const form = new FormData();
  form.append('file', file);

  const headers: Record<string, string> = {};
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (shopId) headers[SHOP_HEADER] = shopId;

  const response = await fetch(`${BASE}${path}`, { method: 'POST', headers, body: form });
  const payload = (await response.json().catch(() => null)) as { data?: UploadedFile; error?: { code: string; message: string } } | null;

  if (!response.ok) {
    throw new ApiClientError(response.status, payload?.error?.code ?? 'UNKNOWN', payload?.error?.message ?? 'Upload failed');
  }
  return (payload as { data: UploadedFile }).data;
}

/**
 * Generic multipart POST with extra form fields — the support desk's uploads
 * carry an `ownerType` (and, on the client portal, a `businessId`) alongside
 * the file, which `uploadFile` above has no slot for.
 */
export async function uploadForm<T>(path: string, form: FormData): Promise<T> {
  const { accessToken, shopId } = getTokens();
  const headers: Record<string, string> = {};
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (shopId) headers[SHOP_HEADER] = shopId;

  const response = await fetch(`${BASE}${path}`, { method: 'POST', headers, body: form });
  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }
  if (!response.ok) {
    const body = payload as { error?: { code: string; message: string } } | null;
    throw new ApiClientError(
      response.status,
      body?.error?.code ?? 'UNKNOWN',
      body?.error?.message ?? 'Upload failed',
    );
  }
  return (payload as { data: T }).data;
}

/**
 * Opens any bearer-authenticated binary path in a new tab — same mechanics as
 * `openFile` below, for routes outside `/files` (the support desk streams its
 * attachments from its own reach-checked endpoint).
 */
export async function openAuthedPath(path: string): Promise<void> {
  const { accessToken, shopId } = getTokens();
  const headers: Record<string, string> = {};
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (shopId) headers[SHOP_HEADER] = shopId;

  const response = await fetch(`${BASE}${path}`, { headers });
  if (!response.ok) throw new ApiClientError(response.status, 'FILE_FETCH_FAILED', 'Could not open that file');
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * Opens `GET /files/:id` in a new tab. Needs its own fetch (not a plain
 * `<a href>`) because the route is bearer-token authenticated — fetch the
 * bytes with the right header, then hand the browser an object URL it can
 * actually navigate to. The URL is revoked on a delay long enough for the
 * new tab to have loaded it, since the blob itself is only ever this small
 * (a payment-proof screenshot), so holding it a few extra seconds costs
 * nothing.
 */
export async function openFile(id: string): Promise<void> {
  const { accessToken, shopId } = getTokens();
  const headers: Record<string, string> = {};
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (shopId) headers[SHOP_HEADER] = shopId;

  const response = await fetch(`${BASE}/files/${id}`, { headers });
  if (!response.ok) throw new ApiClientError(response.status, 'FILE_FETCH_FAILED', 'Could not open that file');

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
